import { useMemo } from 'react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import PropTypes from 'prop-types';
import {Fade} from 'react-awesome-reveal';
import clsx from 'clsx';

// Imports => Constants
import {
  AcBadge,
  DATETIME_FORMATS,
  ICONS,
  KEYS,
  SIZES,
  THEMES,
} from '@opp/ui-kit';

// Imports => Utilities
import {
  AcIsSet,
} from '@opp/ui-kit';
import {
  AcFormatCurrencyAmount,
  AcFormatDate,
  AcFormatInternalURI,
} from '@utils';

// Imports => Atoms
import {
  AcCard,
  AcHeading,
  AcRichContent,
  AcRipple,
  AcIcon,
} from '@opp/ui-kit';
import {useTranslation, withTranslation} from "react-i18next";
import {observer} from "mobx-react-lite";
import {withStore} from "../../stores";
import {AcGetDateIsInFuture} from "../../utilities/ac-format-date";
import moment from "moment/moment";

import { Settlement } from '@src/api/openapi/merchantApi';

const _CLASSES = {
  MAIN: 'ac-settlement-list-item',
  LINK: 'ac-settlement-list-item__link',
  HEADER: 'ac-settlement-list-item__header',
  BODY: 'ac-settlement-list-item__body',
  FOOTER: 'ac-settlement-list-item__footer',
  ICON: 'ac-settlement-list-item__icon',
  TITLE: 'ac-settlement-list-item__title',
  AMOUNT: 'ac-settlement-list-item__amount',
  DESCRIPTION: 'ac-settlement-list-item__description',
  DATETIME: 'ac-settlement-list-item__datetime',
  STATUS: 'ac-settlement-list-item__status',
};

const AcSettlementListItem: React.FC<{ store: any, data: Settlement } & React.HTMLAttributes<HTMLDivElement>>= ({store: {profile}, data, className }) => {
  const {t, i18n} = useTranslation();

  const {current_partner_currency} = profile;

  const getStatusClassNames = useMemo(() => {
    return clsx(_CLASSES.STATUS);
  }, []);

  const getDatetimeClassNames = useMemo(() => {
    return clsx(_CLASSES.DATETIME);
  }, []);

  const getDescriptionClassNames = useMemo(() => {
    return clsx(_CLASSES.DESCRIPTION);
  }, []);

  const getAmountClassNames = useMemo(() => {
    return clsx(_CLASSES.AMOUNT);
  }, []);

  const getTitleClassNames = useMemo(() => {
    return clsx(_CLASSES.TITLE);
  }, []);

  const getIconClassNames = useMemo(() => {
    return clsx(_CLASSES.ICON);
  }, []);

  const getFooterClassNames = useMemo(() => {
    return clsx(_CLASSES.FOOTER);
  }, []);

  const getBodyClassNames = useMemo(() => {
    return clsx(_CLASSES.BODY);
  }, []);

  const getHeaderClassNames = useMemo(() => {
    return clsx(_CLASSES.HEADER);
  }, []);

  const getLinkClassNames = useMemo(() => {
    return clsx(_CLASSES.LINK);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN, className);
  }, [className]);

  const getID = useMemo(() => {
    return data && data.uid;
  }, [data]);

  const getTypeAttributes = useMemo(() => {
    if (!AcIsSet(data)) {
      return null;
    }

    let result = {
      type: null,
      theme: null,
      icon: null,
      status: `${t(`status.${data.status}`, {ns: 'payouts'})}`,
    };

    const {status, payout_type} = data;

    if (payout_type === 'collective') {
      result.icon = ICONS.LAYER_MULTI_OUTLINE;
    } else {
      result.icon = ICONS.LAYER_SINGLE_OUTLINE;
    }

    switch (status) {
      case 'completed':
      case 'paid':
        result.theme = THEMES.CHARLIE_LIGHT;
        break;

      case 'transfer':
        result.theme = THEMES.ALPHA;
        break;

      case 'current':
        result.theme = THEMES.OMEGA_LIGHT;
        break;

      case 'expected':
      case 'next':
        result.theme = THEMES.ECHO;
        break;

      case 'cancelled':
        result.theme = THEMES.BETA_LIGHT;
        break;

      case 'invoiced':
        result.theme = THEMES.ALPHA_DARK;
        break;

      default:
    }

    return result;
  }, [data]);

  const getStatusFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.status)) {
      return null;
    }
    if (!AcIsSet(getTypeAttributes)) {
      return null;
    }

    if (AcIsSet(data.payout_type) && (data.payout_type === 'faster' || data.payout_type === 'instant')) {
      return null;
    }

    const status = getTypeAttributes;

    return <AcBadge className={getStatusClassNames} theme={status.theme}>{status.status}</AcBadge>;
  }, [data]);


  const getPeriodFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.period_start) || !AcIsSet(data.period_end) || !AcIsSet(data.interval)) {
      return null;
    }

    const {period_start, period_end, interval} =
        data;

    let end;

    if (interval === 'continuous' && AcGetDateIsInFuture(moment(period_end * 1000))) {
      end = t('infinite', 'Infinite', {ns: 'payouts'});
    } else {
      end = AcFormatDate(
        period_end * 1000,
        null,
        DATETIME_FORMATS.FULL_DATE_SHORT
      )
    }

    const period = {
      start: AcFormatDate(
        period_start * 1000,
        null,
        DATETIME_FORMATS.FULL_DATE_SHORT
      ),
      end: end
    };

    return (
      <AcRichContent className={getDatetimeClassNames} content={`${period.start} - ${period.end}`} />
    );
  }, [data, i18n.resolvedLanguage]);

  const getDescriptionFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.specifications)) {
      return null;
    }

    const {specifications, total_number_of_transactions} = data;

    const num_specifications = specifications.length;
    let content;
        
    if (num_specifications > 1) {
      content = `${t('contains', 'Contains', {ns: 'payouts'})} ${num_specifications} ${t('profile', {count: num_specifications, ns: 'payouts'})}`;
    }
    else {
      content = `${t('contains', 'Contains', {ns: 'payouts'})} ${total_number_of_transactions} ${t('transaction', {count: total_number_of_transactions, ns: 'payouts'})}`;
    }

    return (
      <AcRichContent className={getDescriptionClassNames} content={content} />
    );

  }, [data]);

  const getAmountFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.total_amount)) {
      return null;
    }

    const { total_amount} = data;

    const content = AcFormatCurrencyAmount(total_amount, current_partner_currency, t.language, true);

    return <AcRichContent className={getAmountClassNames} content={content} />;
  }, [data, i18n.resolvedLanguage, current_partner_currency]);

  const getTitleFormatted = useMemo(() => {

    let result = t('single-payout', 'Single payout', {ns: 'payouts'});

    if (data.payout_type === 'collective') {
      result = t('batch-payout', 'Batch payout', {ns: 'payouts'});
    }

    return (
      <AcHeading rank={6} className={getTitleClassNames}>
        {result}
      </AcHeading>
    );
  }, [data]);

  const getIconFormatted = useMemo(() => {
    if (!AcIsSet(data)) {
      return null;
    }
    if (!AcIsSet(getTypeAttributes)) {
      return null;
    }

    const {icon} = getTypeAttributes;

    if (!AcIsSet(icon)) {
      return null;
    }

    return <AcIcon icon={icon} className={getIconClassNames} />;
  }, [getTypeAttributes]);

  const getSettlementDetailRoute = useMemo(() => {
    if (!AcIsSet(getID)) {
      return null;
    }

    const route = AcFormatInternalURI(data, {
      field: KEYS.UID,
      type: KEYS.SETTLEMENT,
    });

    return route;
  }, [getID, data]);

  return (
    <Fade direction={'up'} duration={300} triggerOnce>
      <AcCard className={getMainClassNames} id={getID}>
        <AcRipple theme={THEMES.SUBTLE} size={SIZES.SMALL} simple />
        <Link
          to={getSettlementDetailRoute}
          className={getLinkClassNames}
          title={t('click-to-view-payout', 'Click to view details of payout {{id}}', {id: `${getID}`, ns: 'payouts'})}
        >
          <div className={getHeaderClassNames}>
            {getIconFormatted}
            {getTitleFormatted}
            {getAmountFormatted}
          </div>
          <div className={getBodyClassNames}>{getDescriptionFormatted}</div>

          <div className={getFooterClassNames}>
            {getPeriodFormatted}
            {getStatusFormatted}
          </div>
        </Link>
      </AcCard>
    </Fade>
  );
};

AcSettlementListItem.propTypes = {
  /**
     * For CSS customization
     */
  className: PropTypes.string,
};

export default withTranslation(['payouts'])(withStore(observer(AcSettlementListItem)));
